import { defineStore } from "pinia";
import api from "@/api";
import {
  AssessmentMastery,
  StudentProgress,
  BaselineCompletion,
} from "@/interfaces/mastery.interface";
import { useLearnerStore } from "./learnerStore";
import { useFilterStore } from "./filterStore";

export const useMasteryStore = defineStore({
  id: "masteryStore",
  state: () => ({
    assessmentMasteryData: {} as AssessmentMastery | null,
    studentProgressData: {} as StudentProgress | null,
    baselineCompletionData: {} as BaselineCompletion | null,
    loader: {
      fetchAssessmentMasteryData: false,
      fetchStudentProgressData: false,
      fetchBaselineCompletionData: false,
    },
  }),

  getters: {
    getAssessmentMasteryData(): AssessmentMastery | null {
      return this.assessmentMasteryData;
    },
    getStudentProgressData(): StudentProgress | null {
      return this.studentProgressData;
    },
    getBaselineCompletionData(): BaselineCompletion | null {
      return this.baselineCompletionData;
    },
    expectedLevelByLearnerId: (state) => (studentId: string) => {
      const learnerStore = useLearnerStore();
      const filterStore = useFilterStore();

      const selectedSubject = filterStore.selectedSubject;

      const levels = {
        MATHS_JUNIOR_HIGH: 9,
        MATHS_SENIOR_PRIMARY: 6,
        MATHS_SENIOR_PRIMARY_V2: 6,
        MATHS_JUNIOR_PRIMARY: 3,
        MATHS_ADULT_NUMERACY: 9,
        ENGLISH_JUNIOR_HIGH: 9,
        ENGLISH_SENIOR_PRIMARY: 6
      };

      const learner = learnerStore.getLearnerById(studentId);

      const currentLearnerAssessmentType = learner?.assessmentStates.find(
        (assessmentState) => assessmentState.subject.toLowerCase() === selectedSubject.name.toLowerCase()
      );

      if (currentLearnerAssessmentType) {
        return levels[currentLearnerAssessmentType.type as keyof typeof levels];
      } else {
        return null;
      }
    }
  },

  actions: {
    /**
     * Fetch assessment mastery data
     */
    async fetchAssessmentMasteryData(payload: any) {
      this.loader.fetchAssessmentMasteryData = true;

      try {
        const response = await api.post(
          "/v2/data/mastery/assessment-mastery-data",
          payload
        );
        if (response.status === 200) {
          this.assessmentMasteryData = response.data.data;
        } else {
          throw new Error("Failed to fetch assessment mastery data.");
        }
      } catch (error) {
        console.error("Error fetching assessment mastery data: ", error);
        this.assessmentMasteryData = null;
      } finally {
        this.loader.fetchAssessmentMasteryData = false;
      }
    },

    /**
     * Fetch student progress data
     */
    async fetchStudentProgressData(payload: any) {
      this.loader.fetchStudentProgressData = true;

      try {
        const response = await api.post(
          "/v2/data/mastery/student-progress-data",
          payload
        );
        if (response.status === 200) {
          this.studentProgressData = response.data.data;
        } else {
          throw new Error("Failed to fetch student progress data.");
        }
      } catch (error) {
        console.error("Error fetching student progress data: ", error);
        this.studentProgressData = null;
      } finally {
        this.loader.fetchStudentProgressData = false;
      }
    },

    /**
     * Fetch baseline completion data
     */
    async fetchBaselineCompletionData(payload: any) {
      this.loader.fetchBaselineCompletionData = true;

      try {
        const response = await api.post(
          "/v2/data/mastery/baseline-completion-data",
          payload
        );
        if (response.status === 200) {
          this.baselineCompletionData = response.data.data;
        } else {
          throw new Error("Failed to fetch baseline completion data.");
        }
      } catch (error) {
        console.error("Error fetching baseline completion data: ", error);
        this.baselineCompletionData = null;
      } finally {
        this.loader.fetchBaselineCompletionData = false;
      }
    },
  },
  persist: true,
});
